import { getChatApiBaseUrl } from '../utils/chatApiBase.js';

const CHAT_API_URL = getChatApiBaseUrl();

class ChatApiService {
  constructor() {
    this.token = null;
  }

  // Получаем токен из localStorage (используется тот же токен, что и в основном проекте)
  getToken() {
    return localStorage.getItem('token');
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async request(endpoint, options = {}, retryCount = 3) {
    const url = `${CHAT_API_URL}${endpoint}`;
    const config = {
      ...options,
      headers: this.getHeaders(),
    };

    // ВАЖНО: POST/PUT/PATCH/DELETE запросы НЕ должны retry'иться —
    // это приводит к дублированию записей (сервер уже обработал первый запрос,
    // но клиент не получил ответ вовремя и отправил второй).
    // Retry только для безопасных/idempotent запросов (GET).
    const method = (config.method || 'GET').toUpperCase();
    const isSafeMethod = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
    const effectiveRetries = isSafeMethod ? retryCount : 1;

    let lastError;

    for (let attempt = 0; attempt < effectiveRetries; attempt++) {
      try {
        const response = await fetch(url, config);
        const data = await response.json();

        if (!response.ok) {
          // Не retry'ить ошибки 4xx
          if (response.status >= 400 && response.status < 500) {
            throw new Error(data.error || 'Ошибка сервера');
          }
          throw new Error(data.error || `HTTP ${response.status}`);
        }

        if (attempt > 0) {
          console.log(`✅ [ChatAPI] Request succeeded after ${attempt} retries`);
        }
        return data;

      } catch (error) {
        lastError = error;
        console.warn(`⚠️ [ChatAPI] Attempt ${attempt + 1}/${effectiveRetries} failed:`, error.message);

        // Не retry'ить клиентские ошибки
        if (error.message && /^(4\d\d|Ошибка сервера)/.test(error.message)) {
          throw error;
        }

        if (attempt < effectiveRetries - 1) {
          const delay = 1000 * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    console.error('❌ [ChatAPI] All retry attempts failed');
    throw lastError;
  }

  // Аутентификация - используем токен из основного проекта
  async getMe() {
    // Всегда получаем данные через API, чтобы получить актуальный avatar
    try {
      const data = await this.request('/auth/me');
      return {
        id: data.userId,
        username: data.username,
        displayName: data.fullName || data.username,
        avatar: data.avatar ?? null
      };
    } catch (error) {
      // Если API не работает, пробуем декодировать токен как fallback
      const token = this.getToken();
      if (!token) {
        throw new Error('Токен не найден');
      }

      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          id: payload.userId,
          username: payload.username,
          displayName: payload.fullName || payload.username,
          avatar: null // Avatar не хранится в токене
        };
      } catch (decodeError) {
        throw error; // Возвращаем оригинальную ошибку API
      }
    }
  }

  // Чат
  async getUsers() {
    return await this.request('/chat/users');
  }

  async getDialogs() {
    return await this.request('/chat/dialogs');
  }

  async getBotCommands(botUserId) {
    return await this.request(`/chat/bot-commands/${botUserId}`);
  }

  async getMessages(userId) {
    return await this.request(`/chat/messages/${userId}`);
  }

  async deleteMessage(messageId) {
    return await this.request(`/chat/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async editMessage(messageId, content) {
    return await this.request(`/chat/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
  }

  async deleteDialog(userId) {
    return await this.request(`/chat/dialogs/user/${userId}`, {
      method: 'DELETE',
    });
  }

  async sendMessage(receiverId, content, messageType = 'text', imageData = null, inReplyToId = null) {
    return await this.request('/chat/messages', {
      method: 'POST',
      body: JSON.stringify({ receiverId, content, messageType, imageData, inReplyToId }),
    });
  }

  async markMessagesAsRead({ senderId, groupId, channelId } = {}) {
    return await this.request('/chat/mark-read', {
      method: 'POST',
      body: JSON.stringify({ senderId, groupId, channelId }),
    });
  }

  async getUnreadCount() {
    return await this.request('/chat/unread-count');
  }

  // Groups
  async createGroup(groupData) {
    return await this.request('/groups/create', {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  }

  async getUserGroups() {
    return await this.request('/groups/list');
  }

  async getGroupDetails(groupId) {
    return await this.request(`/groups/${groupId}`);
  }

  async getGroupMessages(groupId) {
    return await this.request(`/groups/${groupId}/messages`);
  }

  async sendGroupMessage(groupId, content, messageType = 'text', imageData = null, inReplyToId = null) {
    return await this.request('/groups/messages', {
      method: 'POST',
      body: JSON.stringify({ groupId, content, messageType, imageData, inReplyToId }),
    });
  }

  // Channels
  async createChannel(channelData) {
    return await this.request('/channels', {
      method: 'POST',
      body: JSON.stringify(channelData),
    });
  }

  async getUserChannels() {
    return await this.request('/channels');
  }

  async getChannelMessages(channelId) {
    return await this.request(`/channels/${channelId}/messages`);
  }

  async sendChannelMessage(channelId, content, messageType = 'text', imageData = null, audioData = null) {
    return await this.request('/channels/messages', {
      method: 'POST',
      body: JSON.stringify({ channelId, content, messageType, imageData, audioData }),
    });
  }

  async getChannelDetails(channelId) {
    return await this.request(`/channels/${channelId}`);
  }

  async subscribeToChannel(channelId) {
    return await this.request(`/channels/${channelId}/subscribe`, {
      method: 'POST',
    });
  }

  async unsubscribeFromChannel(channelId) {
    return await this.request(`/channels/${channelId}/subscribe`, {
      method: 'DELETE',
    });
  }

  async deleteChannel(channelId) {
    return await this.request(`/channels/${channelId}`, {
      method: 'DELETE',
    });
  }

  async updateChannel(channelId, channelData) {
    return await this.request(`/channels/${channelId}`, {
      method: 'PUT',
      body: JSON.stringify(channelData),
    });
  }

  async searchChannels(query) {
    return await this.request(`/channels/search?query=${encodeURIComponent(query)}`);
  }

  // Forwarding
  async forwardMessage(messageId, receiverIds = [], groupIds = []) {
    return await this.request('/chat/forward', {
      method: 'POST',
      body: JSON.stringify({ messageId, receiverIds, groupIds }),
    });
  }

  // Group management
  async updateGroup(groupId, groupData) {
    return await this.request(`/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData),
    });
  }

  async addGroupMembers(groupId, memberIds) {
    return await this.request(`/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ memberIds }),
    });
  }

  async removeGroupMember(groupId, memberId) {
    return await this.request(`/groups/${groupId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async deleteGroup(groupId) {
    return await this.request(`/groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  async leaveGroup(groupId) {
    return await this.request(`/groups/${groupId}/leave`, {
      method: 'POST',
    });
  }

  // Pinning
  async pinMessage(messageId) {
    return await this.request(`/chat/messages/${messageId}/pin`, {
      method: 'POST',
    });
  }

  async unpinMessage(messageId) {
    return await this.request(`/chat/messages/${messageId}/unpin`, {
      method: 'POST',
    });
  }

  // Profile
  async updateProfile(profileData) {
    return await this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // Reactions
  async addReaction(messageId, emoji) {
    return await this.request(`/chat/messages/${messageId}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    });
  }

  async removeReaction(messageId, emoji) {
    return await this.request(`/chat/messages/${messageId}/reactions`, {
      method: 'DELETE',
      body: JSON.stringify({ emoji }),
    });
  }

  async getMessageReactions(messageId) {
    // Оптимистичные сообщения имеют id вида "temp-...". Для них реакций в БД нет,
    // и запрос на сервер приведёт к ошибке приведения типов (message_id integer).
    const idStr = String(messageId ?? '');
    if (!/^\d+$/.test(idStr)) {
      return {};
    }
    return await this.request(`/chat/messages/${messageId}/reactions`);
  }

  // ─── Стикеры ─────────────────────────────────────────────────────────────────
  async getStickerPacks() {
    return await this.request('/stickers/packs');
  }

  async createStickerPack(name) {
    return await this.request('/stickers/packs', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async renameStickerPack(packId, name) {
    return await this.request(`/stickers/packs/${packId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  }

  async deleteStickerPack(packId) {
    return await this.request(`/stickers/packs/${packId}`, {
      method: 'DELETE',
    });
  }

  async addStickersTopack(packId, filenames) {
    return await this.request(`/stickers/packs/${packId}/stickers`, {
      method: 'POST',
      body: JSON.stringify({ filenames }),
    });
  }

  async deleteSticker(stickerId) {
    return await this.request(`/stickers/${stickerId}`, {
      method: 'DELETE',
    });
  }

  // Возвращает URL стикера (изображение лежит в основном бэкенде)
  getStickerUrl(filename) {
    const mainApiBase = import.meta.env.VITE_API_URL ?? `${window.location.protocol}//${window.location.host}/api`;
    return `${mainApiBase}/uploads/${filename}`;
  }
}

export default new ChatApiService();
